import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useAmenityMasterList,
  useDeleteAmenity,
  useUpdateAmenity,
  type AmenityMasterEntity,
} from "@/hooks/useBusinesses";
import { AmenityEditDialog } from "./AmenityEditDialog";
import { SortableAmenityRow } from "./SortableAmenityRow";

export const AmenitiesSection = () => {
  const { data, isLoading, error } = useAmenityMasterList({
    includeInactive: true,
  });
  const update = useUpdateAmenity();
  const del = useDeleteAmenity();

  const [items, setItems] = useState<AmenityMasterEntity[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<AmenityMasterEntity | null>(null);

  useEffect(() => {
    if (data) setItems(data);
  }, [data]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
  );

  const ids = useMemo(() => items.map((i) => i.id), [items]);

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };
  const openEdit = (a: AmenityMasterEntity) => {
    setEditing(a);
    setDialogOpen(true);
  };

  const handleToggleActive = (a: AmenityMasterEntity, next: boolean) => {
    setItems((prev) =>
      prev.map((i) => (i.id === a.id ? { ...i, isActive: next } : i)),
    );
    update.mutate(
      { id: a.id, data: { isActive: next } },
      {
        onError: (e) => {
          toast.error(e.message);
          setItems((prev) =>
            prev.map((i) =>
              i.id === a.id ? { ...i, isActive: a.isActive } : i,
            ),
          );
        },
      },
    );
  };

  const handleDelete = (a: AmenityMasterEntity) => {
    if (
      !window.confirm(
        `Delete "${a.name}"? In-use amenities will be deactivated instead.`,
      )
    ) {
      return;
    }
    del.mutate(a.id, {
      onSuccess: (res) => {
        if (res.deleted === "soft") {
          toast.success(
            `Deactivated "${a.name}" (used by ${res.referenceCount} business${res.referenceCount === 1 ? "" : "es"})`,
          );
        } else {
          toast.success(`Deleted "${a.name}"`);
        }
      },
      onError: (e) => toast.error(e.message),
    });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((i) => i.id === active.id);
    const newIndex = items.findIndex((i) => i.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;

    const reordered = arrayMove(items, oldIndex, newIndex);
    setItems(reordered);

    const changed = reordered
      .map((item, idx) => ({ item, newSort: idx + 1 }))
      .filter(({ item, newSort }) => item.sortOrder !== newSort);

    Promise.all(
      changed.map(({ item, newSort }) =>
        update.mutateAsync({ id: item.id, data: { sortOrder: newSort } }),
      ),
    ).catch((e) => {
      toast.error(`Reorder failed: ${(e as Error).message}`);
      if (data) setItems(data);
    });
  };

  return (
    <section className="rounded-card border border-border bg-card">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between p-5 border-b border-border">
        <div>
          <h2 className="text-lg font-semibold">Amenities</h2>
          <p className="text-sm text-muted-foreground">
            Master list shown in business wizards. Drag to reorder. Inactive
            amenities are hidden from providers.
          </p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="w-4 h-4 mr-1" />
          Add amenity
        </Button>
      </header>

      {isLoading ? (
        <div className="p-5">
          <Skeleton className="h-32 w-full" />
        </div>
      ) : error ? (
        <p className="p-5 text-sm text-destructive">
          Failed to load: {error.message}
        </p>
      ) : items.length === 0 ? (
        <p className="p-5 text-sm text-muted-foreground">
          No amenities yet. Click "Add amenity" to create the first one.
        </p>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={ids} strategy={verticalListSortingStrategy}>
            <div>
              {items.map((a) => (
                <SortableAmenityRow
                  key={a.id}
                  amenity={a}
                  onEdit={openEdit}
                  onDelete={handleDelete}
                  onToggleActive={handleToggleActive}
                  toggling={update.isPending}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      <AmenityEditDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        amenity={editing}
      />
    </section>
  );
};

export default AmenitiesSection;
